/**
 * Centralized Logger Utility
 * Provides detailed logging across the entire application
 * Respects debug flags from debugConfig
 */

import { isLoggerDebugEnabled, isCategoryEnabled } from './debugConfig';

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

const LOG_LEVELS: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

class Logger {
  private currentLevel: number;
  private enabledCategories: Set<string>;

  constructor() {
    // Enable all logs in development
    this.currentLevel = LOG_LEVELS.DEBUG;
    this.enabledCategories = new Set(['*']); // Enable all categories by default
  }

  setLevel(level: LogLevel): void {
    this.currentLevel = LOG_LEVELS[level] || LOG_LEVELS.DEBUG;
  }

  enableCategory(category: string): void {
    this.enabledCategories.add(category);
  }

  disableCategory(category: string): void {
    this.enabledCategories.delete(category);
  }

  shouldLog(level: LogLevel, category: string): boolean {
    // Check if logger debugging is globally enabled
    if (!isLoggerDebugEnabled()) return false;

    // Check log level
    if (LOG_LEVELS[level] < this.currentLevel) return false;

    // Check if all categories enabled
    if (this.enabledCategories.has('*')) return true;

    // Check specific category
    return this.enabledCategories.has(category);
  }

  formatMessage(level: LogLevel, category: string, message: string, data: any): { prefix: string; message: string; data: any } {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level}] [${category}]`;
    return { prefix, message, data };
  }

  debug(category: string, message: string, data: any = null): void {
    if (!this.shouldLog('DEBUG', category)) return;
    const { prefix, message: msg, data: d } = this.formatMessage('DEBUG', category, message, data);
    console.log(`🔍 ${prefix}`, msg, d || '');
  }

  info(category: string, message: string, data: any = null): void {
    if (!this.shouldLog('INFO', category)) return;
    const { prefix, message: msg, data: d } = this.formatMessage('INFO', category, message, data);
    console.log(`ℹ️ ${prefix}`, msg, d || '');
  }

  warn(category: string, message: string, data: any = null): void {
    if (!this.shouldLog('WARN', category)) return;
    const { prefix, message: msg, data: d } = this.formatMessage('WARN', category, message, data);
    console.warn(`⚠️ ${prefix}`, msg, d || '');
  }

  error(category: string, message: string, error: any = null): void {
    if (!this.shouldLog('ERROR', category)) return;
    const { prefix, message: msg } = this.formatMessage('ERROR', category, message, error);
    console.error(`❌ ${prefix}`, msg, error || '');
    if (error && error.stack) {
      console.error('Stack trace:', error.stack);
    }
  }

  // Special methods for common categories with debug flag checks
  auth(level: LogLevel, message: string, data?: any): void {
    if (!isCategoryEnabled('auth')) return;
    this[level.toLowerCase() as 'debug' | 'info' | 'warn' | 'error']('AUTH', message, data);
  }

  api(level: LogLevel, message: string, data?: any): void {
    if (!isCategoryEnabled('api')) return;
    this[level.toLowerCase() as 'debug' | 'info' | 'warn' | 'error']('API', message, data);
  }

  router(level: LogLevel, message: string, data?: any): void {
    if (!isCategoryEnabled('router')) return;
    this[level.toLowerCase() as 'debug' | 'info' | 'warn' | 'error']('ROUTER', message, data);
  }

  redux(level: LogLevel, message: string, data?: any): void {
    if (!isCategoryEnabled('redux')) return;
    this[level.toLowerCase() as 'debug' | 'info' | 'warn' | 'error']('REDUX', message, data);
  }

  component(componentName: string, level: LogLevel, message: string, data?: any): void {
    if (!isCategoryEnabled('component')) return;
    this[level.toLowerCase() as 'debug' | 'info' | 'warn' | 'error'](`COMPONENT:${componentName}`, message, data);
  }

  lifecycle(componentName: string, method: string, data?: any): void {
    if (!isCategoryEnabled('lifecycle')) return;
    this.debug(`LIFECYCLE:${componentName}`, `${method}()`, data);
  }

  event(eventName: string, data?: any): void {
    if (!isCategoryEnabled('event')) return;
    this.debug('EVENT', eventName, data);
  }

  network(method: string, url: string, data?: any): void {
    if (!isCategoryEnabled('network')) return;
    this.info('NETWORK', `${method} ${url}`, data);
  }

  performance(label: string, duration: number): void {
    if (!isCategoryEnabled('performance')) return;
    this.info('PERFORMANCE', `${label}: ${duration}ms`);
  }
}

// Create singleton instance
const logger = new Logger();

// Enable verbose logging in development
if (process.env.NODE_ENV === 'development') {
  logger.setLevel('DEBUG');
  console.log('🔍 Logger initialized in DEBUG mode');
}

export default logger;
